/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useForm, useFormContext } from '@repo/shared-client/form'
import { useCallback, useEffect } from 'react'

import type { PublicationListFilterFormValues } from './publication-list-filter-types'

export function usePublicationListFilterForm(filterDefaults: PublicationListFilterFormValues) {
  const formMethods = useForm<PublicationListFilterFormValues>({
    defaultValues: filterDefaults,
  })

  useEffect(() => {
    formMethods.reset(filterDefaults, { keepDirtyValues: true })
  }, [filterDefaults, formMethods])

  return formMethods
}

export function usePublicationListFilterFields(onFiltersApply: (values: PublicationListFilterFormValues) => void) {
  const { getValues } = useFormContext<PublicationListFilterFormValues>()

  const triggerFilterChange = useCallback(
    (patch?: Record<string, unknown>) => {
      onFiltersApply({ ...getValues(), ...patch } as PublicationListFilterFormValues)
    },
    [getValues, onFiltersApply]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      triggerFilterChange({ search: value })
    },
    [triggerFilterChange]
  )

  return { triggerFilterChange, handleSearchChange }
}
